import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { NetworkResource, Relationship, MetricDataPoint } from '@shared/types';
import { generateDeepLink } from '@shared/deep-links';
import {
  getResource,
  getResourceRelationships,
  getResourceMetrics,
} from '../lib/api-client';

type MetricRange = '1h' | '24h' | '7d' | '30d';

const RANGE_OPTIONS: { value: MetricRange; label: string }[] = [
  { value: '1h', label: '1h' },
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
];

export function ResourceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [resource, setResource] = useState<NetworkResource | null>(null);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [metrics, setMetrics] = useState<MetricDataPoint[]>([]);
  const [metricRange, setMetricRange] = useState<MetricRange>('1h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    async function fetchResource() {
      setLoading(true);
      setError(null);
      try {
        const [resourceRes, relationshipsRes] = await Promise.all([
          getResource(id!),
          getResourceRelationships(id!),
        ]);
        setResource(resourceRes.data);
        setRelationships(relationshipsRes.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar el recurso');
      } finally {
        setLoading(false);
      }
    }

    fetchResource();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    async function fetchMetrics() {
      try {
        const metricsRes = await getResourceMetrics(id!, metricRange);
        setMetrics(metricsRes.data);
      } catch {
        setMetrics([]);
      }
    }

    fetchMetrics();
  }, [id, metricRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="rounded-lg bg-red-50 p-6 text-center">
        <p className="text-red-700">{error || 'Recurso no encontrado'}</p>
      </div>
    );
  }

  const throughputData = metrics
    .filter((m) => m.metricType === 'throughput')
    .map((m) => ({ timestamp: m.timestamp, value: m.value, unit: m.unit }));

  const latencyData = metrics
    .filter((m) => m.metricType === 'latency')
    .map((m) => ({ timestamp: m.timestamp, value: m.value, unit: m.unit }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{resource.name}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {resource.provider.toUpperCase()} · {resource.resourceType} · {resource.region ?? 'Global'}
          </p>
        </div>
        <DeepLinkButton resource={resource} />
      </div>

      {/* Resource Properties */}
      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Propiedades</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <PropertyItem label="ID" value={resource.id} />
          <PropertyItem label="Proveedor" value={resource.provider.toUpperCase()} />
          <PropertyItem label="Tipo" value={resource.resourceType} />
          <PropertyItem label="Nombre" value={resource.name} />
          <PropertyItem label="ID Nativo" value={resource.nativeId} />
          <PropertyItem label="Región" value={resource.region ?? '—'} />
          <PropertyItem label="Proyecto / Cuenta" value={resource.projectOrAccount} />
          <PropertyItem label="Estado" value={resource.status} />
          <PropertyItem label="Descubierto" value={new Date(resource.discoveredAt).toLocaleString()} />
          <PropertyItem label="Actualizado" value={new Date(resource.updatedAt).toLocaleString()} />
        </dl>
      </section>

      {/* Metadata */}
      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h2>
        {Object.keys(resource.metadata).length === 0 ? (
          <p className="text-sm text-gray-500">Sin metadata disponible</p>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {Object.entries(resource.metadata).map(([key, value]) => (
              <div key={key} className="overflow-hidden">
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{key}</dt>
                <dd className="mt-0.5 text-sm text-gray-900 break-all">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '—')}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      {/* Relationships */}
      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Relaciones</h2>
        {relationships.length === 0 ? (
          <p className="text-sm text-gray-500">Sin relaciones</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {relationships.map((rel) => (
              <li key={rel.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                    {rel.relationshipType}
                  </span>
                  <span className="text-sm text-gray-700">
                    {rel.targetNativeId ?? rel.targetResourceId ?? 'Desconocido'}
                  </span>
                  {rel.targetNotDiscovered && (
                    <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700">
                      No descubierto
                    </span>
                  )}
                </div>
                {rel.targetResourceId && !rel.targetNotDiscovered && (
                  <button
                    onClick={() => navigate(`/inventory/${rel.targetResourceId}`)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    Ver recurso →
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Metrics */}
      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Métricas</h2>
          <div className="flex gap-1">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setMetricRange(opt.value)}
                className={`px-3 py-1 text-sm rounded-md ${
                  metricRange === opt.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {metrics.length === 0 ? (
          <p className="text-sm text-gray-500">No hay métricas disponibles</p>
        ) : (
          <div className="space-y-6">
            {throughputData.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Throughput ({throughputData[0]?.unit ?? ''})
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={throughputData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={(ts: string) =>
                          new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        }
                        fontSize={12}
                      />
                      <YAxis fontSize={12} />
                      <Tooltip
                        labelFormatter={(ts: string) => new Date(ts).toLocaleString()}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="value"
                        name="Throughput"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {latencyData.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Latency ({latencyData[0]?.unit ?? ''})
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={latencyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={(ts: string) =>
                          new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        }
                        fontSize={12}
                      />
                      <YAxis fontSize={12} />
                      <Tooltip
                        labelFormatter={(ts: string) => new Date(ts).toLocaleString()}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="value"
                        name="Latency"
                        stroke="#dc2626"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

/* ─── Sub-Components ──────────────────────────────────────────────────────── */

function PropertyItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 break-all">{value}</dd>
    </div>
  );
}

function DeepLinkButton({ resource }: { resource: NetworkResource }) {
  const providerLabel = resource.provider === 'gcp' ? 'GCP Console' : 'AWS Console';

  // For LB-related resources, always recompute (stored URLs may be outdated or wrong)
  const alwaysRecompute = ['load_balancer', 'target_proxy', 'backend_service', 'url_map', 'dns_policy', 'vpn_gateway', 'nat_gateway', 'route_table', 'target_group', 'psc_service_attachment', 'peering', 'partner_interconnect', 'ncc_hub', 'ncc_spoke'].includes(resource.resourceType);
  const consoleUrl = alwaysRecompute ? generateDeepLink(resource) : resource.consoleUrl;

  if (consoleUrl) {
    return (
      <a
        href={consoleUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        <ExternalLinkIcon />
        Open in {providerLabel}
      </a>
    );
  }

  return (
    <div className="relative group">
      <button
        disabled
        className="inline-flex items-center gap-2 rounded-md bg-gray-300 px-4 py-2 text-sm font-medium text-gray-500 cursor-not-allowed"
      >
        <ExternalLinkIcon />
        {providerLabel}
      </button>
      <div className="absolute right-0 top-full mt-2 hidden group-hover:block z-10 w-64 rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg">
        Enlace no disponible por datos insuficientes
      </div>
    </div>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}
